import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { AdminBookings } from "@/components/admin/AdminBookings";
import { AdminEvents } from "@/components/admin/AdminEvents";
import { AdminInquiries } from "@/components/admin/AdminInquiries";
import { AdminEmbed } from "@/components/admin/AdminEmbed";

const KEY = "gbh_admin_key";
const tabCls = "rounded data-[state=active]:bg-[#141210] data-[state=active]:text-[#F3DDA4]";

const Gate = ({ onOk }) => {
  const [pw, setPw] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/verify", null, { headers: { "X-Admin-Key": pw } });
      sessionStorage.setItem(KEY, pw);
      onOk(pw);
    } catch (err) {
      toast.error(errMsg(err, "Invalid passcode"));
    }
  };
  return (
    <main className="mx-auto max-w-sm px-5 py-28" data-testid="admin-gate">
      <p className="eyebrow">Host portal</p>
      <h1 className="mt-3 font-display text-4xl text-[#1F1B17]">Enter the vault</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <Input type="password" required placeholder="Admin passcode" value={pw} onChange={(e) => setPw(e.target.value)} className="h-12" data-testid="admin-passcode-input" />
        <button type="submit" data-testid="admin-login-button" className="w-full rounded bg-[#141210] py-4 text-sm font-semibold uppercase tracking-widest text-[#F3DDA4] hover:bg-[#2A2520]">Unlock</button>
      </form>
    </main>
  );
};

export default function Admin() {
  const [key, setKey] = useState(sessionStorage.getItem(KEY) || "");
  const [events, setEvents] = useState([]);
  const headers = { "X-Admin-Key": key };

  const logout = () => { sessionStorage.removeItem(KEY); setKey(""); };
  const loadEvents = () => api.get("/admin/events", { headers }).then((r) => setEvents(r.data)).catch((e) => { if (e.response?.status === 401) logout(); else toast.error(errMsg(e)); });

  useEffect(() => {
    if (key) { loadEvents(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!key) return <Gate onOk={setKey} />;

  return (
    <main className="mx-auto max-w-7xl px-5 py-12 sm:px-8" data-testid="admin-dashboard">
      <div className="flex items-end justify-between gap-4">
        <div><p className="eyebrow">Host portal</p><h1 className="mt-2 font-display text-4xl text-[#1F1B17]">Dashboard</h1></div>
        <button type="button" onClick={logout} className="text-xs uppercase tracking-widest text-[#6B6157] hover:text-[#A8832F]" data-testid="admin-logout-button">Lock</button>
      </div>
      <Tabs defaultValue="bookings" className="mt-8">
        <TabsList className="h-auto flex-wrap rounded-none border border-[#E5DACA] bg-white p-1">
          <TabsTrigger className={tabCls} value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger className={tabCls} value="events" data-testid="admin-tab-events">Events</TabsTrigger>
          <TabsTrigger className={tabCls} value="inquiries" data-testid="admin-tab-inquiries">Private requests</TabsTrigger>
          <TabsTrigger className={tabCls} value="embed" data-testid="admin-tab-embed">Share & embed</TabsTrigger>
        </TabsList>
        <TabsContent value="bookings"><AdminBookings headers={headers} events={events} onChange={loadEvents} /></TabsContent>
        <TabsContent value="events"><AdminEvents headers={headers} events={events} reload={loadEvents} /></TabsContent>
        <TabsContent value="inquiries"><AdminInquiries headers={headers} /></TabsContent>
        <TabsContent value="embed"><AdminEmbed events={events} /></TabsContent>
      </Tabs>
    </main>
  );
}
